import { css, keyframes } from 'styled-components'

// corner-shape: squircle is Chromium 139+ only (Safari has it in Technology Preview as of Sept 2026). Elsewhere the
// same radius is drawn as a plain round corner, which reads far rounder than the squircle, so the fallback takes
// about 60% of it, which looks alike: 40 becomes 24, 20 becomes 12. A pill, where the radius is at least half the
// height, passes the same value as the fallback: a smaller round corner would turn it into a rounded rectangle
export const squircle = (radius: number, fallback = Math.round(radius * 0.6)) => css`
  border-radius: ${fallback}px;

  @supports (corner-shape: squircle) {
    corner-shape: squircle;
    border-radius: ${radius}px;
  }
`

export const smallFont = css`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
`

export const wideFont = css`
  ${smallFont};
  font-weight: 700;
  letter-spacing: 0.05em;
`

const fadeKeyframes = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

export const fade = css`
  animation: ${fadeKeyframes} 0.25s;
`

export const transition = (...props: any) => {
  let str = 'transition: '
  props.forEach((item: any, index: any) => {
    str = str.concat(`${item} 0.1s ease${index === props.length - 1 ? ';' : ', '}`)
  })
  return str
}

export const headerItem = css`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0 16px;
`

export const commonModalStyle = css`
  position: fixed;
  z-index: 100;
  left: 0;
  right: 0;
  margin: auto;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 58px - 32px);
  max-height: calc(100dvh - 58px - 32px);
  top: calc(50% + 29px);
  transform: translateY(-50%);
  max-width: calc(100vw - 16px);
  border-radius: 16px;

  @media (max-width: 1024px) {
    top: 74px;
    transform: none;
    max-height: calc(100vh - 90px);
    max-height: calc(100dvh - 90px);
  }
`

export const commonModalContentStyle = css`
  flex: 1;
  min-height: 0;
  overflow: auto;
`

// Brand gradients shared by the sidebar panels and the announcement modals they open.
export const blueGradient = 'linear-gradient(20deg, hsl(206deg 100% 50%), hsl(192deg 98% 52%))'
// The brand gradient turned around for the tall modals: the deep blue at the top, the cyan at the bottom
export const blueGradientDownward = 'linear-gradient(200deg, hsl(206deg 100% 50%), hsl(192deg 98% 52%))'
export const blueGradientHover = 'linear-gradient(20deg, hsl(206deg 100% 55%), hsl(192deg 98% 57%))'
export const greenGradient = 'linear-gradient(20deg, hsl(101deg 58% 36%), hsl(89deg 53% 52%))'
export const greenGradientHover = 'linear-gradient(20deg, hsl(101deg 58% 41%), hsl(89deg 53% 57%))'
export const orangeGradient = 'linear-gradient(135deg, #f2a14b 0%, #ff8b4d 65%, #ff7a50 100%)'

// Shell for the gradient announcement modals (Digitalizare Meniu, Comenzi Online,
// Printare POS, Abonament). shadowColor must be a 6-digit hex; closedTransform is the
// point the close animation shrinks toward (the element that triggered the modal).
export const gradientModalStyle = (gradient: string, shadowColor: string, closedTransform: string) => css`
  ${commonModalStyle}
  width: 607px;
  max-height: calc(100vh - 58px - 96px);
  max-height: calc(100dvh - 58px - 96px);
  background: ${gradient};
  color: #ffffff;
  ${squircle(40)}
  box-shadow:
    0 8px 24px ${shadowColor}52,
    0 2px 6px ${shadowColor}38;
  overflow: hidden;
  transition:
    opacity 0.35s cubic-bezier(0.35, 0, 0.3, 1),
    transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  &.closed {
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    transform: ${closedTransform};
    transition:
      opacity 0.109s linear 0.054s,
      transform 0.35s cubic-bezier(0.4, 0, 0.2, 1),
      visibility 0s linear 0.35s;

    @media (max-width: 1024px) {
      /* Sub 1024px, commonModalStyle anchors the modal at the top without translateY(-50%),
         so the closed transform must not reintroduce it. */
      transform: translate(0, 12vh) scale(0.01);
    }
  }
`

export const gradientModalHeaderStyle = css`
  display: flex;
  width: 100%;
  align-items: flex-start;
  padding: 24px 24px 16px;
  position: relative;
  flex-shrink: 0;

  .title-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-right: 56px;
  }

  .badge {
    display: inline-block;
    vertical-align: middle;
    margin-left: 8px;
    padding: 3px 8px;
    ${squircle(20, 20)}
    background: rgba(255, 255, 255, 0.22);
    border: 1px solid rgba(255, 255, 255, 0.35);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    color: #ffffff;
  }

  .title {
    font-size: 20px;
    font-weight: 700;
    color: #ffffff;
    line-height: 1.2;
  }
`

export const gradientModalScrollbarStyle = css`
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.4) transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.35);
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.55);
  }
`

// Layout and typography for the small gradient pill panels in the left sidebar.
// Consumers set their own background, box-shadow, margin, padding and transition.
export const miniPanelStyle = css`
  width: 100%;
  ${squircle(20, 20)}
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  .label {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .badge {
    padding: 2px 6px;
    ${squircle(10, 10)}
    background: rgba(255, 255, 255, 0.28);
    font-size: 10px;
    font-weight: 700;
    color: #ffffff;
  }

  svg {
    font-size: 12px;
    opacity: 0.8;
  }
`

// The lit 1px rim of the iOS glass look: a conic gradient over the whole shape, masked down to the ring over
// the host's transparent 1px border. The host is position: relative with border: 1px solid transparent, and the
// ring follows the host's corners. --rim-bright, --rim-faint and --rim-spread tune it; --rim-angle (registered in
// styles.css) is where the lit spots start, and a host can animate it
export const glassRim = css`
  --rim-bright: 0.44;
  --rim-faint: 0.075;
  --rim-spread: 45deg;

  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    corner-shape: inherit;
    padding: 1px;
    background: conic-gradient(
      from var(--rim-angle, -45deg),
      rgba(255, 255, 255, var(--rim-faint)) calc(90deg - var(--rim-spread)),
      rgba(255, 255, 255, var(--rim-bright)) 90deg,
      rgba(255, 255, 255, var(--rim-faint)) calc(90deg + var(--rim-spread)),
      rgba(255, 255, 255, var(--rim-faint)) calc(270deg - var(--rim-spread)),
      rgba(255, 255, 255, var(--rim-bright)) 270deg,
      rgba(255, 255, 255, var(--rim-faint)) calc(270deg + var(--rim-spread))
    );
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    mask-composite: exclude;
    pointer-events: none;
  }
`

// iOS glass, the web menu's options disc recipe: a tint over a blurred, saturated backdrop, a lit 1px rim
// drawn by ::before, white text, a quick shrink on press. Whoever uses it sets --tint for its own colour
export const glass = css`
  ${glassRim}
  --tint: rgba(69, 79, 91, 0.55);
  position: relative;
  border: 1px solid transparent; /* the rim is drawn by ::before */
  border-radius: 999px;
  background: var(--tint);
  -webkit-backdrop-filter: blur(6px) saturate(1.8);
  backdrop-filter: blur(6px) saturate(1.8);
  box-shadow: 0 0 18px rgba(0, 0, 0, 0.25);
  color: #fff;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent; /* iOS Safari's grey flash on tap, inherited by the label inside */
  transition:
    background 300ms,
    transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

  /* Under a real pointer it grows a touch, the mirror of the press. Not on touch screens, where a tap
     would leave the hover look stuck until the next tap elsewhere */
  @media (hover: hover) {
    &:hover {
      --tint: rgba(69, 79, 91, 0.7);
      transform: scale(1.05);
    }
  }

  /* Pressed, it shrinks quickly and springs back on release */
  &:active {
    transform: scale(0.95);
    transition:
      background 300ms,
      transform 0.1s ease-out;
  }
`

// The lit spots slide one way along the card and back, like the web menu's search pill
const rimSwing = keyframes`
  from {
    --rim-angle: -85deg;
  }
  to {
    --rim-angle: -5deg;
  }
`

// A dark frosted squircle for the gradient announcement modals (Abonament, Ce e nou): no tint of its own, the
// darkening is done by the backdrop filter, so the glass stays true to the colour under it. The lit rim comes
// from the shared glass recipe
export const darkGlassCard = css`
  position: relative;
  ${glassRim}
  border: 1px solid transparent; /* the rim is drawn by ::before */
  ${squircle(40)}
  -webkit-backdrop-filter: blur(12px) brightness(0.9);
  backdrop-filter: blur(12px) brightness(0.9);
  animation: ${rimSwing} 5s ease-in-out infinite alternate;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1);

  /* The modals stay mounted while closed: cards are not worth animating unseen */
  .closed & {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
